import { createFileRoute, Link } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, Pre, Automate, H3 } from '../components/docs/prose'
import { NetWorthDiagram } from '../components/docs/NetWorthDiagram'

export const Route = createFileRoute('/docs/accounts')({ component: Accounts })

const SECTIONS = [
  { id: 'your-accounts', title: 'The three you create' },
  { id: 'net-worth', title: 'How net worth is computed' },
  { id: 'credit-normal', title: 'Why debts are negative' },
  { id: 'system-accounts', title: 'System accounts' },
  { id: 'opening-balances', title: 'Opening balances' },
  { id: 'manual-assets', title: 'Assets no bank reports' },
]

function Accounts() {
  return (
    <DocPage
      path="/docs/accounts"
      title="Accounts"
      lede={
        <>
          There are six account types in the database and you only ever create
          three of them. The other three are bookkeeping counterparties that
          FolioWorth manages itself.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="your-accounts" title="The three you create">
        <DefList
          items={[
            [
              <C>Asset</C>,
              'Things you own that hold a cash value: chequing, savings, the house, the car. Positive balances.',
            ],
            [
              <C>Investment</C>,
              'Registered and non-registered holdings: RRSP, TFSA, RESP, brokerage. Separated from Asset so allocation and cost-base reporting have something to select on.',
            ],
            [
              <C>Debt</C>,
              'What you owe: mortgage, credit cards, line of credit, car loan. Stored negative. Paying one down moves it toward zero.',
            ],
          ]}
        />
        <P>
          Each account has its own currency. Lines are stored in that currency
          and converted to your household base currency for anything that adds
          accounts together.
        </P>
      </Section>

      <Section id="net-worth" title="How net worth is computed">
        <Pre>{`net worth = Assets + Investments + Debts`}</Pre>
        <P>
          That is an addition, not a subtraction, and the difference matters.
          Because debts already carry a negative sign, adding them is the whole
          calculation. A report that subtracted a separately-maintained debt
          total would be one refactor away from subtracting it twice.
        </P>

        <NetWorthDiagram />

        <P>
          Balances are never stored. <C>account_balances</C> is a view that
          sums ledger lines per account, and net worth is computed from that
          view. Nothing to invalidate, nothing to recompute nightly, nothing to
          drift.
        </P>
        <H3>Two ways to ask</H3>
        <P>
          The ledger series (<C>/net-worth/ledger</C>) walks a running
          cumulative sum per month-end and is exact for any date the ledger
          covers. The snapshot series takes the most recent balance snapshot
          per account instead, which is what you want for accounts you update
          by hand every few months rather than transaction by transaction.
        </P>
      </Section>

      <Section id="credit-normal" title="Why debts are negative">
        <P>
          &ldquo;Credit-normal&rdquo; means an account whose usual balance is a
          credit — negative under our sign convention. It sounds like an
          accounting quibble and it is really a data-modelling decision: it
          removes a class of bug entirely.
        </P>
        <P>
          If a mortgage were stored as +329,000, every screen that touches it
          would need to know to flip it. Miss one and net worth is off by twice
          the mortgage. Store it as &minus;329,000 and there is nothing to
          remember: the sign is in the data, and every consumer just adds.
        </P>
        <P>
          The flip happens once, at the edge, for display — the net worth API
          negates the debt total so the UI can print a positive number under a
          heading that already says &ldquo;Debts&rdquo;.
        </P>
      </Section>

      <Section id="system-accounts" title="System accounts">
        <P>
          Double-entry needs somewhere for the other leg to land when money
          enters or leaves the household entirely. Your salary does not come
          from another account you own. Groceries do not go to one. Those legs
          land in system accounts.
        </P>
        <DefList
          items={[
            [<C>Income</C>, 'Where money entering the household comes from. Seeded with Income: Salary, Income: Interest and Income: Uncategorised.'],
            [<C>Expense</C>, 'Where money leaving the household goes. Seeded with Expense: Uncategorised; more are created as categories are used.'],
            [<C>Equity</C>, 'The counterparty for money that was already there before FolioWorth existed. One account: Equity: Opening Balances.'],
          ]}
        />
        <P>
          These are excluded from net worth. They are not things the household
          owns or owes; they are the record of value crossing the boundary.
          Counting <C>Income: Salary</C> as an asset would mean your net worth
          grew by every dollar you ever earned and never spent a cent of it.
        </P>
        <P>
          You cannot create one. The API validates account type against the
          three real types and rejects <C>Income</C>, <C>Expense</C> and{' '}
          <C>Equity</C>, so a system account can only ever come from
          FolioWorth&rsquo;s own get-or-create path. There is also a partial
          unique index on their names, which makes that get-or-create safe to
          run concurrently — two imports racing to create{' '}
          <C>Expense: Groceries</C> cannot produce two of them.
        </P>
      </Section>

      <Section id="opening-balances" title="Opening balances">
        <P>
          When you add an account with a starting balance, that balance has to
          be double-entered like anything else. The other leg goes to{' '}
          <C>Equity: Opening Balances</C>.
        </P>
        <Pre>{`Opening   Chequing                   +4,200
          Equity: Opening Balances   -4,200`}</Pre>
        <P>
          This is why an opening balance does not appear as income in your
          reports. It is not money you earned this year — it is money that
          already existed, and equity is the accounting word for
          &ldquo;already existed&rdquo;.
        </P>
      </Section>

      <Automate>
        Connecting a bank creates accounts from the institution&rsquo;s own
        list, with the type inferred from Plaid&rsquo;s: credit and loan
        accounts become <C>Debt</C>, brokerage and investment accounts become{' '}
        <C>Investment</C>, everything else becomes <C>Asset</C>. Opening
        balances come across with them. See{' '}
        <Link to="/docs/automating" style={{ color: 'var(--accent-text)' }}>Automating it</Link>.
      </Automate>
      <Section id="manual-assets" title="Assets no bank reports">
        <P>
          A house, a car, a private holding, a pension entitlement &mdash; nobody
          syncs these, so you enter them, and the figure is only as good as the
          day you entered it. Add one from <strong>Accounts &rarr; Add
          Account</strong> with its type set to Asset, or Debt for the loan
          against it.
        </P>
        <P>
          Two fields exist that a synced account does not need:
        </P>
        <DefList
          items={[
            [
              'Value as of',
              'The date the figure was assessed. A house entered at $1.75M in 2026 and still showing $1.75M in 2031 is stale, and nothing on screen would say so. The date makes the age of the number visible.',
            ],
            [
              'Where the value came from',
              'A municipal assessment, a dealer quote, a comparable sale. Optional, and it is what makes the figure defensible a year later when you have forgotten.',
            ],
          ]}
        />
        <P>
          Vehicles can also carry a <strong>VIN</strong>, validated on entry
          (seventeen characters, no I, O or Q) so a transposed digit surfaces
          while you are still standing next to the car. It is stored in plain
          text, unlike an account number: a VIN is visible through the
          windshield and grants access to nothing.
        </P>
        <P>
          A debt is entered as a <strong>positive</strong> number &mdash; how much
          you owe, the way a loan statement reads &mdash; and stored negative.
          Asking for a minus sign invites the one typo that turns a debt into an
          asset and moves net worth by twice the balance.
        </P>
        <H3>Revaluing later</H3>
        <P>
          Updating a value posts the <em>difference</em> against equity rather
          than overwriting the balance. The ledger stays balanced, and the
          history of what something was said to be worth, and when, survives the
          restatement. Revaluations are excluded from the money-flow map:
          valuing a house is not income, and left in, one valuation swamps every
          real flow in the month.
        </P>
      </Section>

    </DocPage>
  )
}
